import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { courseAPI } from '../services/api';
import { toast } from 'react-toastify';
import { FiSave, FiEye, FiUpload, FiVideo, FiFileText } from 'react-icons/fi';

function AddContent() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [course, setCourse] = useState(null);
  const [folders, setFolders] = useState([]);
  const [allFolders, setAllFolders] = useState([]);
  
  // Form States
  const [folderForm, setFolderForm] = useState({ folder: '', folderid: '' });
  const [subfolderForm, setSubfolderForm] = useState({ parent_folder_id: '', folder_id: '' });
  const [videoForm, setVideoForm] = useState({
    video_folder_id: '',
    title: '',
    video_id: '',
    description: '',
    live_date: '',
    vid: ''
  });
  const [documentForm, setDocumentForm] = useState({
    folder_id: '',
    doc_name: '',
    description: '',
    document: null,
    docid: ''
  });
  
  const [submitting, setSubmitting] = useState(false);
  
  useEffect(() => {
    fetchData();
  }, [courseId]);
  
  const fetchData = async () => {
    try {
      const response = await courseAPI.getAddContentData(courseId);
      if (response.data.success) {
        setCourse(response.data.data.course);
        setFolders(response.data.data.folders || []);
      }
      
      // Fetch all folders for nested dropdown
      const foldersResponse = await courseAPI.getNestedFolders(courseId);
      if (foldersResponse.data.success) {
        setAllFolders(foldersResponse.data.folders || []);
      }
      
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load course data');
      setLoading(false);
    }
  };
  
  const handleCreateFolder = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      // Match admin panel field names: 'id', 'folder', 'folderid'
      const data = {
        id: courseId,
        folder: folderForm.folder,
        folderid: folderForm.folderid || ''
      };
      
      const response = await courseAPI.createMainFolder(data);
      if (response.data.success) {
        toast.success(response.data.message);
        setFolderForm({ folder: '', folderid: '' });
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save folder');
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleCreateSubfolder = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      // Match admin panel field names: 'course_id', 'folder_id' (for folder name!), 'parent_folder_id'
      const data = {
        course_id: courseId,
        folder_id: subfolderForm.folder_id, // This is the folder NAME, not ID (admin panel naming)
        parent_folder_id: subfolderForm.parent_folder_id
      };
      
      const response = await courseAPI.createSubfolder(data);
      if (response.data.success) {
        toast.success(response.data.message);
        setSubfolderForm({ parent_folder_id: '', folder_id: '' });
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create subfolder');
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleAddVideo = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const data = {
        course_id: courseId,
        folder_id: videoForm.video_folder_id,
        title: videoForm.title,
        video_id: videoForm.video_id,
        description: videoForm.description,
        live_date: videoForm.live_date || null,
        vid: videoForm.vid || null
      };
      
      const response = await courseAPI.addVideo(data);
      if (response.data.success) {
        toast.success(response.data.message);
        setVideoForm({
          video_folder_id: '',
          title: '',
          video_id: '',
          description: '',
          live_date: '',
          vid: ''
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to add video');
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleAddDocument = async (e) => {
    e.preventDefault();
    setSubmitting(false);
    
    try {
      const formData = new FormData();
      formData.append('course_id', courseId);
      formData.append('folder_id', documentForm.folder_id);
      formData.append('doc_name', documentForm.doc_name);
      formData.append('description', documentForm.description || '');
      
      if (documentForm.docid) {
        formData.append('docid', documentForm.docid);
        if (documentForm.document) {
          formData.append('document', documentForm.document);
        }
      } else {
        // For new documents, support multiple files
        const fileInput = document.getElementById('document');
        if (fileInput.files.length > 0) {
          for (let i = 0; i < fileInput.files.length; i++) {
            formData.append('document[]', fileInput.files[i]);
          }
        } else {
          toast.error('Please select at least one document');
          setSubmitting(false);
          return;
        }
      }
      
      const response = await courseAPI.addDocument(formData);
      if (response.data.success) {
        toast.success(response.data.message);
        setDocumentForm({
          folder_id: '',
          doc_name: '',
          description: '',
          document: null,
          docid: ''
        });
        document.getElementById('document').value = '';
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to add document');
    } finally {
      setSubmitting(false);
    }
  };
  
  // Render nested folders as options
  const renderFolderOptions = (folders, level = 0) => {
    const options = [];
    const spacing = '\u00A0'.repeat(level * 2); // Non-breaking spaces for indentation
    
    folders.forEach(folder => {
      if (folder.parent_folder_id === '0' || folder.parent_folder_id === 0 || folder.parent_folder_id === null) {
        options.push(
          <option key={folder.folder_id} value={folder.folder_id}>
            {spacing}{folder.folders}
          </option>
        );
        
        // Find children
        const children = folders.filter(f => f.parent_folder_id === folder.folder_id);
        if (children.length > 0) {
          options.push(...renderFolderOptionsRecursive(children, folders, level + 1));
        }
      }
    });
    
    return options;
  };
  
  const renderFolderOptionsRecursive = (children, allFolders, level) => {
    const options = [];
    const spacing = '\u00A0'.repeat(level * 2);
    
    children.forEach(child => {
      options.push(
        <option key={child.folder_id} value={child.folder_id}>
          {spacing}{child.folders}
        </option>
      );
      
      const grandChildren = allFolders.filter(f => f.parent_folder_id === child.folder_id);
      if (grandChildren.length > 0) {
        options.push(...renderFolderOptionsRecursive(grandChildren, allFolders, level + 1));
      }
    });
    
    return options;
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }
  
  return (
    <div className="container mx-auto px-4 py-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          <FiUpload className="inline-block mr-2" />
          Add Contents - {course?.title}
        </h1>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sidebar Actions */}
        <div className="lg:col-span-1 space-y-4">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Contents</h3>
            {folders.length === 0 ? (
              <p className="text-gray-600 text-center mb-4">
                You haven't uploaded any content yet. Select the content from the right panel to start adding here.
              </p>
            ) : (
              <p className="text-sm text-gray-600 mb-4">{folders.length} folder(s) created</p>
            )}
            <button
              onClick={() => navigate(`/courses/${courseId}/viewcontent`)}
              className="w-full px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
            >
              <FiEye /> View Contents And Folders
            </button>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Import Contents</h3>
            <button
              onClick={() => navigate(`/courses/${courseId}/import`)}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            >
              Import Contents
            </button>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Add Demo Video</h3>
            <button
              onClick={() => navigate(`/courses/${courseId}/demovideo`)}
              className="w-full px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors flex items-center justify-center gap-2"
            >
              <FiVideo /> Add Demo Video
            </button>
          </div>
        </div>
        
        {/* Main Content Forms */}
        <div className="lg:col-span-2 space-y-6">
          {/* Create Folder */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Create Folder For {course?.title}</h3>
            </div>
            <div className="p-6">
              <form onSubmit={handleCreateFolder}>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Create Folder <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      value={folderForm.folder}
                      onChange={(e) => setFolderForm({ ...folderForm, folder: e.target.value })}
                      required
                      placeholder="Enter folder name"
                    />
                  </div>
                </div>
                <div className="mt-4 flex justify-end">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                  >
                    <FiSave /> {submitting ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </form>
            </div>
          </div>
          
          {/* Create Subfolder */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Sub Folders</h3>
            </div>
            <div className="p-6">
              <form onSubmit={handleCreateSubfolder}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Parent <span className="text-red-500">*</span>
                    </label>
                    <select
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      value={subfolderForm.parent_folder_id}
                      onChange={(e) => setSubfolderForm({ ...subfolderForm, parent_folder_id: e.target.value })}
                      required
                    >
                      <option value="">Select Folder</option>
                      {renderFolderOptions(allFolders)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Child <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      value={subfolderForm.folder_id}
                      onChange={(e) => setSubfolderForm({ ...subfolderForm, folder_id: e.target.value })}
                      required
                      placeholder="Enter child folder name"
                    />
                  </div>
                </div>
                <div className="mt-4 flex justify-end">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                  >
                    <FiSave /> {submitting ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </form>
            </div>
          </div>
          
          {/* Add Video */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Add Video</h3>
            </div>
            <div className="p-6">
              <form onSubmit={handleAddVideo}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Folder <span className="text-red-500">*</span>
                    </label>
                    <select
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      value={videoForm.video_folder_id}
                      onChange={(e) => setVideoForm({ ...videoForm, video_folder_id: e.target.value })}
                      required
                    >
                      <option value="">Select Folder</option>
                      {renderFolderOptions(allFolders)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Title <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      value={videoForm.title}
                      onChange={(e) => setVideoForm({ ...videoForm, title: e.target.value })}
                      required
                      placeholder="Enter video title"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Video <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      value={videoForm.video_id}
                      onChange={(e) => setVideoForm({ ...videoForm, video_id: e.target.value })}
                      required
                      placeholder="Enter youtube id (Ex - J3Q-QQfUkOY)"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      value={videoForm.description}
                      onChange={(e) => setVideoForm({ ...videoForm, description: e.target.value })}
                      required
                      placeholder="Enter description"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Live Date
                    </label>
                    <input
                      type="datetime-local"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      value={videoForm.live_date}
                      onChange={(e) => setVideoForm({ ...videoForm, live_date: e.target.value })}
                    />
                  </div>
                </div>
                <div className="mt-4 flex justify-end">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                  >
                    <FiSave /> {submitting ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </form>
            </div>
          </div>
          
          {/* Add Documents */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Add Documents</h3>
            </div>
            <div className="p-6">
              <form onSubmit={handleAddDocument}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Folder <span className="text-red-500">*</span>
                    </label>
                    <select
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      value={documentForm.folder_id}
                      onChange={(e) => setDocumentForm({ ...documentForm, folder_id: e.target.value })}
                      required
                    >
                      <option value="">Select Folder</option>
                      {renderFolderOptions(allFolders)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      value={documentForm.doc_name}
                      onChange={(e) => setDocumentForm({ ...documentForm, doc_name: e.target.value })}
                      required
                      placeholder="Enter document name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Document <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="document"
                      type="file"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      multiple={!documentForm.docid}
                      accept=".jpg,.jpeg,.png,.webp,.pdf,.docx"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Allowed: jpg, jpeg, png, webp, pdf, docx
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      value={documentForm.description}
                      onChange={(e) => setDocumentForm({ ...documentForm, description: e.target.value })}
                      required
                      placeholder="Enter description"
                    />
                  </div>
                </div>
                <div className="mt-4 flex justify-end">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                  >
                    <FiFileText /> {submitting ? 'Uploading...' : 'Upload Document'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddContent;

